import { h } from '@contensis/canvas-html';
import { tryStringify } from '~/util/tryparse';

/**
 * Take raw Contensis entry values and return simple html snippets
 * to be parsed by html-canvas package and converted to valid canvas json
 */
class CanvasHtml {
  encode = (input: string) => {
    return h.text(tryStringify(input));
  };

  // use the render function to render any array values with no fuss
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  render = (input: any, fn: Function, separator = '') => {
    let markup = '';

    if (Array.isArray(input)) {
      let pos = 0;
      const len = input.length;
      for (const each of input) {
        markup += fn(each);
        if (++pos !== len) markup += separator;
      }
    } else markup += fn(input);

    return markup;
  };

  // just render the raw value(s)
  raw = (input: any) => this.render(input, (val: any) => val);

  // use the tag function to wrap input in any html tag
  tag = (input: any, tag = 'p', attributes?: string) => {
    return this.render(input, (val: any) => {
      if (['boolean', 'number'].includes(typeof val) || val)
        return `<${tag}${attributes ? ` ${attributes}` : ''}>${tryStringify(val)}</${tag}>`;
      return '';
    });
  };

  component = (input: any, componentId: string) => {
    return this.render(input, (val: any) => {
      const value = this.encode(val);
      return `<div class='component' data-component='${componentId}' data-component-value='${value}'></div>`;
    });
  };

  entry = (input: any) => {
    return this.render(input, (val: any) => {
      const id = val?.sys?.id;
      const value = h.text(
        tryStringify({
          sys: {
            id: val?.sys?.id,
            contentTypeId: val?.sys?.contentTypeId,
            dataFormat: val?.sys?.dataFormat,
          },
        })
      );
      return `<a class='inline-entry' data-entry='${value}'/></a>`;
    });
  };

  image = (input: any) => {
    return this.render(input, (val: any) => {
      if (val?.asset)
        return `<img src='${val.asset.sys.uri}' alt='${
          val.altText || val.asset.altText || ''
        }' title='${val.caption || val.asset.caption || ''}'/>`;
      return '';
    });
  };

  quote = (input: any) => {
    return this.render(input, (val: any) => {
      if (val)
        return `<figure><blockquote>${val.text}</blockquote>${
          val.source ? `<figcaption>${val.source}</figcaption>` : ''
        }</figure>`;
      return '';
    });
  };
}

export const canvasHtml = new CanvasHtml();
